import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { Modal, Tooltip } from "antd";
import {
  ArrowRightOutlined,
  CheckOutlined,
  LoadingOutlined,
  SearchOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../../modalPrimitives";
import { useUsers } from "../../../../../hooks/useUsers";
import { useWaiterOpenCounts } from "../../../../../hooks/useBills";
import { useViewport } from "../../../../../hooks/useViewport";
import { initialsOf } from "../waiterInitials";
import {
  gridArrowIndex,
  isArrowKey,
  LEAVE_FIELD_ON_ARROWS,
  scrollMarkedIntoView,
  takeKeyFromField,
} from "../listKeyboard";
import {
  ModalHeader,
  ModalFooter,
  GhostButton,
  PrimaryButton,
} from "../../modalPrimitives";

interface TransferWaiterModalProps {
  open: boolean;
  onBack: () => void;
  onSubmit: (waiterId: number | null) => void;
  currentWaiterId: number | null;
  submitting?: boolean;
  title?: string;
  subtitle?: string;
  confirmLabel?: string;
  userRole?: string;
  /** Сумата, чиято отговорност се прехвърля — подзаглавието „Отговорността за X EUR…". */
  transferAmount?: number;
  /** „Прехвърли всичките ми N маси" — показва се само когато е подадено и N ≥ 2. */
  onTransferAll?: (waiterId: number) => void;
  myOpenTablesCount?: number;
  /** Подсказка, когато „Прехвърли всичките ми маси" е заключено (локален
   *  режим, docs/edge.md) — бутонът стои, но е disabled. */
  transferAllLockedTooltip?: string;
}

export const TransferWaiterModal = ({
  open,
  onBack,
  onSubmit,
  currentWaiterId,
  submitting = false,
  title,
  subtitle,
  confirmLabel,
  userRole,
  transferAmount,
  onTransferAll,
  myOpenTablesCount = 0,
  transferAllLockedTooltip,
}: TransferWaiterModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet, isDesktop } = useViewport();
  const { data: users = [], isLoading, error } = useUsers(userRole, undefined, undefined, { enabled: open });
  // Брой отворени маси на всеки сервитьор — само докато модалът е отворен.
  const { data: openCounts = [] } = useWaiterOpenCounts(open);
  const openCountByWaiter = useMemo(
    () => new Map(openCounts.map((row) => [row.waiterId, row.count])),
    [openCounts],
  );

  const [selectedWaiterId, setSelectedWaiterId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setSelectedWaiterId(null);
      setSearchTerm("");
    }
  }, [open]);

  const filteredWaiters = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return users
      .filter((u) => {
        if (!term) return true;
        const label = (u.names || u.username || "").toLowerCase();
        return label.includes(term);
      })
      .sort((a, b) => {
        // Текущият сервитьор отива най-отгоре
        if (a.id === currentWaiterId) return -1;
        if (b.id === currentWaiterId) return 1;
        const an = (a.names || a.username || "").toLowerCase();
        const bn = (b.names || b.username || "").toLowerCase();
        return an.localeCompare(bn);
      });
  }, [users, searchTerm, currentWaiterId]);

  const handleConfirm = useCallback(() => {
    if (selectedWaiterId === null) return;
    onSubmit(selectedWaiterId);
  }, [selectedWaiterId, onSubmit]);

  // Клавиатурата (десктоп): буквите отиват в търсачката; ↑ ↓ от нея излизат в
  // списъка и маркират (от „нищо" — първия), извън нея ← → също, Backspace
  // трие от търсенето, а буква връща в полето (`listKeyboard.ts`). Enter
  // прехвърля на маркирания — без избор нищо, защото най-отгоре е текущият.
  useEffect(() => {
    if (!open || !isDesktop) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
        e.preventDefault();
        if (e.target instanceof HTMLInputElement) {
          setSearchTerm("");
          (e.target as HTMLElement)?.blur?.();
          return;
        }
        onBack();
        return;
      }
      if (!takeKeyFromField(e)) return;
      if (isArrowKey(e.key)) {
        e.preventDefault();
        if (filteredWaiters.length === 0) return;
        const index = filteredWaiters.findIndex((w) => w.id === selectedWaiterId);
        setSelectedWaiterId(
          filteredWaiters[gridArrowIndex(e.key, index, filteredWaiters.length, 1)].id,
        );
        return;
      }
      if (e.key === "Enter") {
        if (selectedWaiterId === null) return;
        e.preventDefault();
        handleConfirm();
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        setSearchTerm((prev) => prev.slice(0, -1));
        inputRef.current?.focus();
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setSearchTerm((prev) => prev + e.key);
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [open, handleConfirm, onBack, isDesktop, selectedWaiterId, filteredWaiters]);

  // Маркираният — във видимата част на списъка (скролът е скрит).
  useEffect(() => {
    scrollMarkedIntoView(listRef.current, `[data-waiter-id="${selectedWaiterId}"]`);
  }, [selectedWaiterId]);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPadClass = isNarrow ? "px-4" : "px-6";

  const displayName = (user: { names: string | null; username: string }) =>
    user.names || user.username;
  const fromUser = users.find((u) => u.id === currentWaiterId);
  const toUser = users.find((u) => u.id === selectedWaiterId);
  // Първото име в бутона („Прехвърли на Гергана") — цялото име не се събира.
  const toFirstName = toUser ? displayName(toUser).split(" ")[0] : null;
  const showTransferAll = !!onTransferAll && myOpenTablesCount >= 2;

  const headerSubtitle =
    subtitle ??
    (transferAmount !== undefined
      ? t("splitBill.transferWaiterResponsibility", {
          total: transferAmount.toFixed(2),
        })
      : t("splitBill.transferWaiterSubtitle", {
          count: filteredWaiters.length,
        }));

  return (
    <Modal
      open={open}
      onCancel={onBack}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div className="flex max-h-[85vh] flex-col bg-pos-card text-pos-ink">
        <ModalHeader
          icon={<UserSwitchOutlined />}
          title={title ?? t("splitBill.transferWaiterTitle")}
          subtitle={headerSubtitle}
          onClose={onBack}
          isNarrow={isNarrow}
        />

        <div
          className={`${bodyPadClass} flex min-h-0 flex-1 flex-col gap-3 pt-4 pb-3`}
        >
          {/* От кого → на кого */}
          <div className="flex h-11 shrink-0 items-center gap-3 rounded-[10px] border border-pos-line3 bg-pos-panel px-4 text-sm font-semibold text-pos-ink2">
            {/* „От"/„На" стоят плътно до името; по-широкият отстъп е само около стрелката */}
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-pos-soft2">
                {t("splitBill.transferWaiterFrom")}
              </span>
              <span className="min-w-0 truncate text-pos-ink">
                {fromUser ? displayName(fromUser) : "—"}
              </span>
            </span>
            <ArrowRightOutlined className="shrink-0 text-[14px] text-pos-dim" />
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="shrink-0 text-pos-soft2">
                {t("splitBill.transferWaiterTo")}
              </span>
              <span
                className={`min-w-0 truncate ${
                  toUser ? "text-pos-accent-ink" : "text-pos-muted"
                }`}
              >
                {toUser
                  ? displayName(toUser)
                  : t("splitBill.transferWaiterNoTarget")}
              </span>
            </span>
          </div>

          {/* Търсачка — същата кутия като в каталога */}
          <label className="flex h-11 shrink-0 items-center gap-3 rounded-[10px] border border-pos-line3 bg-pos-card px-4">
            <SearchOutlined className="text-[17px] text-pos-dim" />
            <input
              ref={inputRef}
              {...LEAVE_FIELD_ON_ARROWS}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("splitBill.transferWaiterSearch")}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-base font-semibold text-pos-ink outline-none placeholder:text-pos-muted md:text-sm"
            />
          </label>

          <div className="min-h-[120px] max-h-[50vh] flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {isLoading ? (
              <Center>
                <LoadingOutlined spin className="text-[36px] text-pos-accent" />
              </Center>
            ) : error ? (
              <Center tone="danger">{String(error)}</Center>
            ) : filteredWaiters.length === 0 ? (
              <Center>{t("splitBill.transferWaiterNone")}</Center>
            ) : (
              <div ref={listRef} className="flex flex-col gap-1.5">
                {filteredWaiters.map((w) => {
                  const openTables = openCountByWaiter.get(w.id) ?? 0;
                  return (
                    <WaiterRow
                      key={w.id}
                      waiterId={w.id}
                      selected={selectedWaiterId === w.id}
                      label={displayName(w)}
                      meta={
                        openTables === 1
                          ? t("splitBill.transferWaiterTablesOne")
                          : t("splitBill.transferWaiterTables", {
                              count: openTables,
                            })
                      }
                      badge={
                        w.id === currentWaiterId
                          ? t("splitBill.transferWaiterCurrent")
                          : null
                      }
                      onClick={() => setSelectedWaiterId(w.id)}
                    />
                  );
                })}
              </div>
            )}
          </div>

          {showTransferAll && (
            <TransferAllButton
              label={t("ordering.transferAllBills", {
                count: myOpenTablesCount,
              })}
              disabled={selectedWaiterId === null || submitting}
              lockedTooltip={transferAllLockedTooltip}
              onClick={() =>
                selectedWaiterId !== null && onTransferAll?.(selectedWaiterId)
              }
            />
          )}
        </div>

        <ModalFooter isNarrow={isNarrow} columns="1fr 3fr">
          <GhostButton
            label={t("common.cancel")}
            onClick={onBack}
            shortcut={isDesktop ? "Esc" : undefined}
          />
          <PrimaryButton
            label={
              toFirstName
                ? t("splitBill.transferWaiterConfirmTo", { name: toFirstName })
                : (confirmLabel ?? t("splitBill.transferWaiterConfirm"))
            }
            onClick={handleConfirm}
            disabled={selectedWaiterId === null || submitting}
            loading={submitting}
            shortcut={isDesktop ? "⏎" : undefined}
          />
        </ModalFooter>
      </div>
    </Modal>
  );
};

const Center = ({
  children,
  tone,
}: {
  children: React.ReactNode;
  tone?: "danger";
}) => (
  <div
    className={`flex min-h-[200px] items-center justify-center text-[13px] ${
      tone === "danger" ? "text-pos-danger-ink" : "text-pos-muted"
    }`}
  >
    {children}
  </div>
);

const WaiterRow = ({
  waiterId,
  selected,
  label,
  meta,
  badge,
  onClick,
}: {
  /** За превъртането до маркирания (`data-waiter-id`). */
  waiterId: number;
  selected: boolean;
  label: string;
  meta: string;
  badge: string | null;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={selected}
    data-waiter-id={waiterId}
    className={`flex w-full cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-2.5 text-left transition-colors ${
      selected
        ? "border-pos-accent bg-pos-marked"
        : "border-pos-line3 bg-pos-panel hover:bg-pos-canvas"
    }`}
  >
    <span
      className={`grid h-9 w-9 shrink-0 place-items-center rounded-full pt-[2px] font-pos-mono text-[15px] font-bold leading-none ${
        selected
          ? "bg-pos-accent text-white"
          : "bg-pos-addon-bg text-pos-addon-ink"
      }`}
    >
      {initialsOf(label)}
    </span>
    <span className="flex min-w-0 flex-1 flex-col">
      <span
        className={`truncate text-sm font-semibold ${
          selected ? "text-pos-accent-ink" : "text-pos-ink"
        }`}
      >
        {label}
      </span>
      <span className="truncate text-[13px] font-medium text-pos-soft2">{meta}</span>
    </span>
    {badge && (
      <span className="shrink-0 rounded-full border border-pos-line bg-pos-card px-3 py-1 text-sm font-semibold text-pos-ink2">
        {badge}
      </span>
    )}
    {selected && (
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-pos-accent text-[12px] text-white">
        <CheckOutlined />
      </span>
    )}
  </button>
);

/** „Прехвърли всичките ми N маси": при `lockedTooltip` бутонът е disabled,
 * а подсказката е на обвиващия span — disabled бутон не получава mouse
 * събития, затова той е pointer-events-none. */
const TransferAllButton = ({
  label,
  disabled,
  lockedTooltip,
  onClick,
}: {
  label: string;
  disabled: boolean;
  lockedTooltip?: string;
  onClick: () => void;
}) => {
  const button = (
    <button
      type="button"
      disabled={disabled || !!lockedTooltip}
      onClick={onClick}
      className={`h-11 w-full shrink-0 cursor-pointer rounded-[10px] border border-pos-ctl bg-transparent px-4 text-sm font-semibold text-pos-ink2 transition-colors hover:bg-pos-canvas disabled:cursor-not-allowed disabled:opacity-45 ${
        lockedTooltip ? "pointer-events-none" : ""
      }`}
    >
      {label}
    </button>
  );
  return lockedTooltip ? (
    <Tooltip title={lockedTooltip}>
      <span className="grid shrink-0 cursor-not-allowed">{button}</span>
    </Tooltip>
  ) : (
    button
  );
};
